import { isPlatformBrowser } from '@angular/common';
import {
  afterNextRender,
  Component,
  computed,
  inject,
  PLATFORM_ID,
  signal,
} from '@angular/core';

type StorePlatform = 'ios' | 'android' | null;

function detectMobileStorePlatform(): StorePlatform {
  const ua = navigator.userAgent;
  const isIos =
    /iPhone|iPad|iPod/i.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/i.test(ua);

  if (isIos) return 'ios';
  if (isAndroid) return 'android';
  return null;
}

@Component({
  selector: 'app-cdai-download',
  imports: [],
  templateUrl: './cdai-download.component.html',
})
export class CdaiDownloadComponent {
  private readonly platformId = inject(PLATFORM_ID);

  readonly screenshotSrc = '/images/cdai-download/cdai-app-store.webp';
  readonly iosStoreButtonSrc = '/images/cdai-download/ios-app-store-button.svg';
  readonly googlePlayButtonSrc =
    '/images/cdai-download/google-play-store-button.svg';

  readonly iosStoreUrl =
    'https://apps.apple.com/us/app/career-driver-ai/id6754710168';
  readonly googlePlayUrl =
    'https://play.google.com/store/apps/details?id=ai.careerdriver.careerdriver';

  /** null = desktop / unknown — both buttons equal */
  readonly storePlatform = signal<StorePlatform>(null);

  readonly iosButtonClass = computed(() =>
    this.buttonClass(this.storePlatform() === 'ios')
  );
  readonly androidButtonClass = computed(() =>
    this.buttonClass(this.storePlatform() === 'android')
  );
  readonly iosImageClass = computed(() =>
    this.imageClass(this.storePlatform() === 'ios')
  );
  readonly androidImageClass = computed(() =>
    this.imageClass(this.storePlatform() === 'android')
  );

  constructor() {
    afterNextRender(() => {
      if (!isPlatformBrowser(this.platformId)) return;
      this.storePlatform.set(detectMobileStorePlatform());
    });
  }

  private buttonClass(isPrimary: boolean): string {
    const platform = this.storePlatform();
    const base =
      'inline-block transition-all duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-900';

    if (platform === null) {
      return `${base} hover:scale-105`;
    }

    if (isPrimary) {
      return `${base} order-1 scale-110 hover:scale-[1.15]`;
    }

    return `${base} order-2 opacity-40 grayscale hover:opacity-70 hover:grayscale-0 hover:scale-105`;
  }

  private imageClass(isPrimary: boolean): string {
    const platform = this.storePlatform();

    if (platform === null) {
      return 'h-10 w-auto';
    }

    return isPrimary ? 'h-12 w-auto' : 'h-8 w-auto';
  }
}
